import { useEffect, useMemo, useState } from 'react';
import { BadgePlus, Cable, Clock3, Trash2 } from 'lucide-react';

const pluralize = (noun, amount) => {
  const label = noun || 'attempt';
  if (Number(amount) === 1) return label;
  if (label.endsWith('s')) return label;
  if (label.endsWith('y')) return `${label.slice(0, -1)}ies`;
  return `${label}s`;
};

const hustleIcon = (id) => id === 'streetlight_copper' ? <Cable /> : id === 'dumpster_diving' ? <Trash2 /> : <BadgePlus />;

export function V2HustleWindow({ hustles, busy, onSearch }) {
  const items = Array.isArray(hustles?.items) ? hustles.items : [];
  const [selectedId, setSelectedId] = useState(items[0]?.id || '');
  const selected = useMemo(() => items.find((item) => item.id === selectedId) || items[0] || null, [items, selectedId]);

  useEffect(() => {
    if (items.length && !items.some((item) => item.id === selectedId)) setSelectedId(items[0].id);
  }, [items, selectedId]);

  if (!items.length) return <div className="v2-window-empty">No hustles are available in this run right now.</div>;
  const noun = selected?.attemptNoun || 'attempt';

  return <div className="v2-hustle-window">
    <nav className="v2-hustle-list" aria-label="Available hustles">
      {items.map((item) => {
        const itemNoun = item.attemptNoun || 'attempt';
        return <button key={item.id} className={selected?.id === item.id ? 'active' : ''} onClick={() => setSelectedId(item.id)}>
          <strong>{item.title}</strong>
          <small>{item.ready ? `${item.attemptsRemaining} ${pluralize(itemNoun, item.attemptsRemaining)} ready` : `Cooldown · Day ${item.nextAvailableDay}`}</small>
        </button>;
      })}
    </nav>
    {selected && <section className="v2-hustle-detail">
      <div className="v2-hustle-title-row">{hustleIcon(selected.id)}<div><h2>{selected.title}</h2><p>{selected.description}</p></div></div>
      <div className="v2-hustle-status">
        <span><Clock3 /> Cooldown <b>{selected.cooldownDays} advancing day{selected.cooldownDays === 1 ? '' : 's'}</b></span>
        <span>{pluralize(noun, 2)} left <b>{selected.attemptsRemaining}/{selected.attemptsPerCycle}</b></span>
        {Number(selected.cashCost || 0) > 0 && <span>Cost <b>${selected.cashCost}</b></span>}
      </div>
      {!selected.ready && <div className="v2-window-notice">This hustle is cooling down. Available again on <strong>Day {selected.nextAvailableDay}</strong>.</div>}
      {selected.ready && selected.canAfford === false && <div className="v2-window-notice">You need <strong>${selected.cashCost}</strong> cash to start this hustle.</div>}
      {selected.selectionLabel && <p className="v2-hustle-selection-label">{selected.selectionLabel}</p>}
      <div className="v2-hustle-workarea">
        <div className={`v2-hustle-sites ${selected.sites?.length === 1 ? 'single' : ''}`}>
          {(selected.sites || []).map((site) => {
            const searched = selected.searchedSiteIds?.includes(site.id);
            return <button key={site.id} disabled={busy || searched || !selected.ready || selected.canAfford === false || selected.attemptsRemaining <= 0} onClick={() => onSearch(selected.id, site.id)}>
              <strong>{site.title}</strong>
              {!searched && (site.riskLabel || site.payoutLabel) && <span>{site.riskLabel && <em>{site.riskLabel}</em>}{site.payoutLabel && <b>{site.payoutLabel}</b>}</span>}
              <small>{searched ? 'DONE THIS CYCLE' : site.description}</small>
            </button>;
          })}
        </div>
        {selected.lastResult && <div className={`v2-hustle-result ${selected.lastResult.tone || ''}`}><strong>{selected.lastResult.siteTitle}</strong><p>{selected.lastResult.message}</p></div>}
      </div>
    </section>}
  </div>;
}
